<template lang="pug">
	.audio-tracker-compressor.flex-col.full-width.space-large.center.align-center
		.flex-col.space-large.flex-1
			.flex-row
				.flex-col.align-center.space.volume-control
					.upper.font-small {{$tCaption('In')}}
					AudioTrackerSliderButton(orientation="vertical", length="200px", v-model="values.compressorInputGain", :step="0.01", :round="2", :min="0", :max="1")
				.graph.flex-row.align-center.center.flex-1
					svg(:width="`${svgWidth}px`", :height="`${svgHeight}px`")
						path(v-for="path in graphPaths", :d="path" stroke="#f27649" fill="transparent" stroke-width="2")
				.flex-col.align-center.space.volume-control
					.upper.font-small {{$tCaption('Out')}}
					AudioTrackerSliderButton(orientation="vertical", length="200px", v-model="values.compressorOutputGain", :step="0.01", :round="2", :min="0", :max="1")
				.flex-col.space.align-center
					.font-tiny.upper.grey {{$tCaption('Pre')}}
					AudioTrackerVUMeter(ref="VUMeter", orientation="vertical", :tickVisible="false", size="200px", thickness="12px", :isPlaying="tracker.isPlaying", :filters="{ stereoAnalyser: instance && instance.getPreAnalyser() }")
				.flex-col.space.align-center
					.font-tiny.upper.grey {{$tCaption('Post')}}
					AudioTrackerVUMeter(ref="VUMeter", orientation="vertical", :tickVisible="false", size="200px", thickness="12px", :isPlaying="tracker.isPlaying", :filters="{ stereoAnalyser: instance && instance.getPostAnalyser() }")
				.flex-col.space.align-center(:title="$tCaption('Attenuation')")
					.font-tiny.upper.grey {{$tCaption('Atten.')}}
					AudioTrackerVUMeter(ref="VUMeter", orientation="vertical", :tickVisible="false", size="200px", thickness="12px", :isPlaying="tracker.isPlaying", :min="0", :max="20", :redPoint="18", :yellowPoint="15", 
						:valueResolver="() => instance && instance.getAttenuation()")
			.flex-row.center
				RoundButton(ref="threshold", v-model="values.threshold", :min="-100", :max="0", :step="1", :title="$tCaption('Threshold')", :width="buttonSize", :unit="$tCaption('dB')")
				RoundButton(ref="knee", v-model="values.knee", :min="0", :max="40", :step="1",  :title="$tCaption('Knee')", color="#8d90ff", :stepSensivity="2", :indicators="[0,5,15,30,40]", :width="buttonSize", :unit="$tCaption('dB')")
				RoundButton(ref="ratio", v-model="values.ratio", :min="1", :max="20", :step="values.ratio < 5 ? 0.1 : values.ratio < 10 ? 0.2 : 0.5", :title="$tCaption('Ratio')", color="#68b3e4", 
					:stepSensivity="values.ratio < 5 ? 1 : values.ratio < 10 ? 2 : 3", :round="1", :indicators="[0,12,20]", :width="buttonSize", :unit="$tCaption('dB')")
				RoundButton(ref="attack", v-model="values.attack", :min="0", :max="1", :step="0.001", :title="$tCaption('Attack (sec.)')", :stepSensivity="1", :round="3", :width="buttonSize",)
				RoundButton(ref="release", v-model="values.release", :min="0", :max="1", :step="0.001",  :title="$tCaption('Release (sec.)')", :stepSensivity="2", :round="3", :width="buttonSize")
				RoundButton(ref="makeUpGain", v-model="values.compressorMakeUpGain", :min="0", :max="20", :step="0.01", :title="$tCaption('Make-up gain')", :stepSensivity="0.2", color="#5ed134" :round="2", :width="buttonSize", :unit="$tCaption('dB')")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import RoundButton from '../AudioTrackerRoundButton.vue';
	import AudioTrackerSliderButton from '../AudioTrackerSliderButton.vue';
	import AudioTrackerVUMeter from '../AudioTrackerVUMeter.vue';
	import audioHelper from 'view/audioTracker/audio-helper';

	export default {
		translationCategory: 'audioTracker.AudioTrackerCompressor',
		name: 'audio-tracker-compressor',
		components: { RoundButton, AudioTrackerSliderButton, AudioTrackerVUMeter },
		props: {
			playing: VueTypes.bool.def(false),
			tracker: VueTypes.any.def(null),
			instance: VueTypes.any.def(null)
		},
		data() {
			return {
				svgWidth: 620,
				svgHeight: 200,
				buttonSize: 90,
				preMetering: -60,
				postMetering: -60
			};
		},
		computed: {
			graphPaths() {
				return this.getGraphsPath();
			},
			values: {
				get: function() { return this.instance.values },
				set: function(v) { 
					this.instance.values = v;
				}
			}
		},
		watch: {
			values: {
				handler(v) {
					this.$emit('change', v);
					this.$applyValues();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.wapiCompressor = this;
			this.$applyValues = _.debounce(this.applyValues, 50, {
				leading: false,
				trailing: true,
				maxWait: 200
			});
		},
		wapiInstance(opts) {
			if (__DEBUG__)
				console.debug('Instantiate compressor');

			const values = {
				threshold: 0,
				knee: 30,
				ratio: 12,
				attack: 0.003,
				release: 0.25,
				compressorInputGain: 1,
				compressorMakeUpGain: 1,
				compressorOutputGain: 1
			};

			let compressorInputGainNode = null;
			let compressorNode = null;
			let compressorMakeUpGainNode = null;
			let compressorOutputGainNode = null;
			let preAnalyserNode = null;
			let postAnalyserNode = null;

			const applyValuesToPipeline = (v) => {
				if (compressorNode) {
					for(let key in v) {
						if (compressorNode[key] && v[key] != null) {
							compressorNode[key].value = v[key];
						}
					}
				}
				if (compressorInputGainNode) {
					compressorInputGainNode.gain.value = v.compressorInputGain;
				}
				if (compressorMakeUpGainNode) {
					compressorMakeUpGainNode.gain.value = v.compressorMakeUpGain;
				}
				if (compressorOutputGainNode) {
					compressorOutputGainNode.gain.value = v.compressorOutputGain;
				}
			}

			return {
				getPreAnalyser() {
					return preAnalyserNode;
				},
				getPostAnalyser() {
					return postAnalyserNode;
				},
				getNodes: () => ({
					compressorInputGainNode,
					compressorNode,
					compressorMakeUpGainNode,
					compressorOutputGainNode
				}),
				values,
				component: () => opts.component,
				getValues: () => values,
				setValues: (v) => {
					_.merge(values, v);
					applyValuesToPipeline(values);
				},
				getUI: () => ({
					width: 880,
					height: 520
				}),
				getAudioPipeline(opts) {
					const audioContext = opts?.audioContext || new AudioContext();

					preAnalyserNode = audioHelper.StereoAnalyserNode(audioContext);
					preAnalyserNode.fftSize = 512;

					postAnalyserNode = audioHelper.StereoAnalyserNode(audioContext);
					postAnalyserNode.fftSize = 512;

					compressorNode = audioContext.createDynamicsCompressor();
					compressorNode.threshold.value = 0;
					compressorNode.knee.value = 30;
					compressorNode.ratio.value = 12;
					compressorNode.attack.value = 0.003;
					compressorNode.release.value = 0.25;

					compressorInputGainNode = audioContext.createGain();
					compressorMakeUpGainNode = audioContext.createGain();
					compressorOutputGainNode = audioContext.createGain();
					
					compressorInputGainNode.connect(preAnalyserNode);
					preAnalyserNode.connect(compressorNode);
					compressorNode.connect(compressorMakeUpGainNode);
					compressorMakeUpGainNode.connect(compressorOutputGainNode);
					compressorOutputGainNode.connect(postAnalyserNode);

					return {
						input: compressorInputGainNode,
						output: postAnalyserNode
					}
				},
				setPlayState(isPlaying) {
				},
				getAttenuation() {
					return  Math.abs(compressorNode.reduction);
				}
			}
		},
		methods: {
			getGraphsPath() {
				var getY = y => this.svgHeight - y;
				var kneeCoef = this.values.knee / 40;
				var yCoef = this.svgHeight / this.svgWidth;
				var kneeX = kneeCoef * this.svgWidth / 2;
				var thresholdX = this.svgWidth - this.svgWidth * Math.abs(this.values.threshold) / 100;
				var x1 = Math.max(0, thresholdX - kneeX);
				var y1 = getY(x1 * yCoef);

				var ratioThreshold = (this.svgWidth - thresholdX) / this.values.ratio;
				var yLast = getY((thresholdX + ratioThreshold) * yCoef);

				var x2 = Math.min(x1 + kneeX * 2, this.svgWidth);// + kneeX * 2;
				var y2 = Math.max(yLast, getY(x2 * yCoef + kneeCoef * (x2 - thresholdX) / 6 - (x2 - thresholdX) * yCoef / (1 + 1 / this.values.ratio)));

				var result = [];

				result.push(`M 0 ${getY(0)} L ${x1} ${y1}`);
				result.push(`M ${x1} ${y1} C ${thresholdX} ${getY(thresholdX * yCoef)}, ${thresholdX} ${getY(thresholdX * yCoef)}, ${x2} ${y2}`);
				result.push(`M ${x2} ${y2} L ${this.svgWidth} ${yLast}`);

				return result;
			},
			applyValues() {
				if (this.instance) {
					this.instance.setValues(this.values);
				}
			},
			updateMeters() {
				//Pre metering
				this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);
				let sampleBuffer = this.preFloatTimeData;
				let peakInstantaneousPower = 0;
				for (let i = 0; i < sampleBuffer.length; i++) {
					const power = sampleBuffer[i] ** 2;
					peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
				}
				this.preMetering = this.convertGainToDecibels(peakInstantaneousPower);

				//Post metering
				this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);
				sampleBuffer = this.postFloatTimeData;
				peakInstantaneousPower = 0;
				for (let i = 0; i < sampleBuffer.length; i++) {
					const power = sampleBuffer[i] ** 2;
					peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
				}
				this.postMetering = this.convertGainToDecibels(peakInstantaneousPower);
			},
			convertGainToDecibels(gain) {
				return 20 * Math.log10(gain);
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-compressor {
		@import "main/themes/generated/variables.base.scss";

		.graph {
			> svg {
				background: rgba(255, 255, 255, 0.02);
				background-image: linear-gradient(rgba(255, 255, 255, 0.04) .1em, transparent .05em), linear-gradient(90deg, rgba(255, 255, 255, 0.05) .05em, transparent .1em);
				background-size: 4em 2em;
				border-right: 1px solid rgba(255, 255, 255, 0.04);
				border-bottom: 1px solid rgba(255, 255, 255, 0.04);
				border-radius: 4px;
			}
		}

		.volume-control {
			min-width: 30px;
		}
	}
</style>
